import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Empty, Table, Typography } from "antd";
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import type { Table2DComponentSpec, Table2DDataSpec } from "../Processing/types";
import { Table2DHeatmap } from "./Table2DHeatmap";
import { liveColWidth, table2dPinnedAsRows } from "./table2dGrid";
import { formatToDecimals, inferDecimalsFromLabels } from "../Processing/decimals";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";
import { resolveTemplate } from "../Utils/resolveTemplate";

const { Text } = Typography;

const DEFAULT_HEX = "#CCCCCC";

export function Table2DWidget({ id }: { id: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const data = useWidgetData(id);
  const isTable2D = data?.type === "table_2d";
  const spec = isTable2D ? (data.spec as Table2DComponentSpec) : null;
  const table2d = isTable2D ? (data as any).table2d as Table2DDataSpec | null : null;
  const slicerValues = useSlicerVariables();
  const title = resolveTemplate(spec?.display?.title ?? "", slicerValues).resolved || "2D Table";
  const isPlainTable = isTable2D && spec != null && !spec.colorize;

  // Floor the antd table body's height to fill the widget. As a MIN-height it only
  // affects short tables (few rows): their body grows so the horizontal scrollbar
  // drops to the widget's bottom. Tall tables already exceed it, so their wrapper
  // vertical scroll (and scrollbar style) is untouched. We never change overflow.
  const [bodyMinH, setBodyMinH] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isPlainTable) {
      setBodyMinH(undefined);
      return;
    }
    const measure = () => {
      const widgetHeader = (el.querySelector(".widget-header") as HTMLElement | null)?.offsetHeight ?? 0;
      const antdHeader = (el.querySelector(".ant-table-header") as HTMLElement | null)?.offsetHeight ?? 39;
      setBodyMinH(Math.max(0, el.clientHeight - widgetHeader - antdHeader - 8));
    };
    measure();
    const raf = requestAnimationFrame(measure); // re-measure once antd renders its header
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isPlainTable]);

  const tableData = useMemo(() => {
    if (!table2d || !spec || spec.colorize) return null;

    const { row_keys, row_labels, col_keys, col_labels, cells, pinned_best, pinned_avg, pinned_total } = table2d;
    const pinnedGoAsRows = table2dPinnedAsRows(spec.rowDimension, spec.colDimension);
    const isMetricDim = spec.rowDimension === "metric" || spec.colDimension === "metric";
    const hasPinned = !isMetricDim && (spec.pinnedCols.best || spec.pinnedCols.avg || spec.pinnedCols.total);

    type CellEntry = { value: number | null; hex: string | undefined; label: string | undefined };
    const cellMap: Record<string, Record<string, CellEntry>> = {};
    const rowKeySet = new Set(row_keys);
    const colKeySet = new Set(col_keys);
    for (const cell of cells) {
      if (!rowKeySet.has(cell.row_key) || !colKeySet.has(cell.col_key)) {
        console.warn(`Table2DWidget: skipping cell with unrecognised key (row="${cell.row_key}", col="${cell.col_key}")`);
        continue;
      }
      if (!cellMap[cell.row_key]) cellMap[cell.row_key] = {};
      const hex = cell.hex && cell.hex.toUpperCase() !== DEFAULT_HEX ? cell.hex : undefined;
      cellMap[cell.row_key][cell.col_key] = { value: cell.value, hex, label: cell.value_label ?? undefined };
    }

    const hasAnyColor = Object.values(cellMap).some((row) =>
      Object.values(row).some((entry) => entry.hex != null),
    );

    // The backend labels each cell to the metric's decimal count. The pinned
    // Best/Avg/Sum arrive as plain numbers, so format them to the same count.
    const pinDecimals = inferDecimalsFromLabels(cells.map((cell) => cell.value_label));
    const fmtPinned = (v: number | null) =>
      v == null ? "" : pinDecimals != null ? formatToDecimals(v, pinDecimals) : String(v);

    const colorRender = (v: number | null, record: Record<string, any>, ck: string) => {
      if (v == null) return "";
      const text = (record[`__label_${ck}`] as string | undefined) ?? String(v);
      const hex = hasAnyColor ? (record[`__color_${ck}`] as string | undefined) : undefined;
      return hex
        ? <span style={{ color: hex, fontWeight: 600 }}>{text}</span>
        : text;
    };

    const antdCols: any[] = [
      {
        key: "__row",
        title: "",
        dataIndex: "__row",
        fixed: "left" as const,
        width: 100,
        render: (v: string) => v,
      },
    ];

    // Pinned Best/Avg as extra columns (primary axis on rows)
    if (hasPinned && !pinnedGoAsRows) {
      if (spec.pinnedCols.best && pinned_best) {
        antdCols.push({ key: "__best", title: "Best", dataIndex: "__best", width: 72, align: "right" as const, render: fmtPinned });
      }
      if (spec.pinnedCols.avg && pinned_avg) {
        antdCols.push({ key: "__avg", title: "Avg", dataIndex: "__avg", width: 72, align: "right" as const, render: fmtPinned });
      }
      if (spec.pinnedCols.total && pinned_total) {
        antdCols.push({ key: "__total", title: "Sum", dataIndex: "__total", width: 72, align: "right" as const, render: fmtPinned });
      }
    }

    // A numeric column keeps its fixed width. A text answer label (an options-type
    // wellness question) widens its column so the answer reads on one line.
    const labelsOf = (ck: string) =>
      row_keys.map((rk) => cellMap[rk]?.[ck]?.label ?? "").filter((label) => label !== "");
    for (const ck of col_keys) {
      antdCols.push({
        key: ck,
        title: col_labels[ck] ?? ck,
        dataIndex: ck,
        width: liveColWidth(labelsOf(ck)),
        align: "right" as const,
        render: (v: number | null, record: Record<string, any>) => colorRender(v, record, ck),
      });
    }

    const rows: any[] = row_keys.map((rk) => {
      const row: Record<string, any> = { key: rk, __row: row_labels[rk] ?? rk };
      for (const ck of col_keys) {
        const entry = cellMap[rk]?.[ck];
        row[ck] = entry?.value ?? null;
        if (entry?.hex) row[`__color_${ck}`] = entry.hex;
        if (entry?.label != null) row[`__label_${ck}`] = entry.label;
      }
      if (hasPinned && !pinnedGoAsRows) {
        if (spec.pinnedCols.best && pinned_best) row["__best"] = pinned_best[rk] ?? null;
        if (spec.pinnedCols.avg && pinned_avg) row["__avg"] = pinned_avg[rk] ?? null;
        if (spec.pinnedCols.total && pinned_total) row["__total"] = pinned_total[rk] ?? null;
      }
      return row;
    });

    // Pinned Best/Avg/Sum as extra rows on top (primary axis on cols)
    if (hasPinned && pinnedGoAsRows) {
      const pinnedRows: any[] = [];
      if (spec.pinnedCols.best && pinned_best) {
        const row: Record<string, any> = { key: "__best", __row: "Best" };
        for (const ck of col_keys) {
          row[ck] = pinned_best[ck] ?? null;
          row[`__label_${ck}`] = fmtPinned(row[ck]);
        }
        pinnedRows.push(row);
      }
      if (spec.pinnedCols.avg && pinned_avg) {
        const row: Record<string, any> = { key: "__avg", __row: "Avg" };
        for (const ck of col_keys) {
          row[ck] = pinned_avg[ck] ?? null;
          row[`__label_${ck}`] = fmtPinned(row[ck]);
        }
        pinnedRows.push(row);
      }
      if (spec.pinnedCols.total && pinned_total) {
        const row: Record<string, any> = { key: "__total", __row: "Sum" };
        for (const ck of col_keys) {
          row[ck] = pinned_total[ck] ?? null;
          row[`__label_${ck}`] = fmtPinned(row[ck]);
        }
        pinnedRows.push(row);
      }
      rows.unshift(...pinnedRows);
    }

    return { columns: antdCols, rows };
  }, [table2d, spec]);

  // No data rows means nothing was found in the range — only pinned Best/Avg/Sum
  // rows would render otherwise, so show the same notification as the other widgets.
  const hasData = table2d != null && table2d.row_keys.length > 0;

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={<Text type="secondary" style={{ fontSize: 12 }}>No data found in the selected date range</Text>}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}
    />
  );

  return (
    <div
      className="table-widget"
      ref={containerRef}
      data-grid-id={id}
      style={bodyMinH != null ? ({ ["--t2d-body-h"]: `${bodyMinH}px` } as CSSProperties) : undefined}
    >
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Text strong style={{ fontSize: 18 }}>{title}</Text>
          <WidgetInfoButton id={id} />
        </span>
      </div>
      {spec?.colorize ? (
        hasData ? (
          <Table2DHeatmap table2d={table2d!} spec={spec} showDataLabels={spec.showDataLabels} />
        ) : (
          emptyState
        )
      ) : (
        hasData && tableData ? (
          <Table
            className="table-2d-widget"
            columns={tableData.columns}
            dataSource={tableData.rows}
            pagination={false}
            size="small"
            bordered={false}
            scroll={{ x: "max-content", y: "100%" }}
            style={{ width: "100%" }}
          />
        ) : (
          emptyState
        )
      )}
    </div>
  );
}
